import { useEffect, useRef, useState } from 'react';
import { api } from '../../services/api';
import type { PackagePreviewRequest, PackagePreviewResponse } from '../../types/packages';

export const PREVIEW_DEBOUNCE_MS = 400;

export interface PackagePreviewState {
  /** The last response; check `fresh` before trusting it for the current form. */
  data: PackagePreviewResponse | null;
  /** True when `data` was computed for exactly the current request. */
  fresh: boolean;
  loading: boolean;
  error: boolean;
}

/**
 * Spec 081b — the server prices the draft (derived mode, totals, saving, every error) 400 ms after
 * the last item/price change. Older responses never overwrite newer ones; a null request clears nothing
 * and calls nothing (fewer than one item).
 */
export function usePackagePreview(token: string | null | undefined, request: PackagePreviewRequest | null): PackagePreviewState {
  const key = request ? JSON.stringify(request) : null;
  const [data, setData] = useState<PackagePreviewResponse | null>(null);
  const [dataKey, setDataKey] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const seq = useRef(0);

  useEffect(() => {
    if (!token || !key) {
      setLoading(false);
      return undefined;
    }
    const mySeq = ++seq.current;
    const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
    setLoading(true);
    const timer = setTimeout(() => {
      api.previewPackage(JSON.parse(key) as PackagePreviewRequest, token, controller?.signal)
        .then((res) => {
          if (mySeq !== seq.current) return;
          setData(res);
          setDataKey(key);
          setError(false);
        })
        .catch((err) => {
          if (mySeq !== seq.current || (err instanceof DOMException && err.name === 'AbortError')) return;
          setError(true);
        })
        .finally(() => { if (mySeq === seq.current) setLoading(false); });
    }, PREVIEW_DEBOUNCE_MS);
    return () => {
      clearTimeout(timer);
      controller?.abort();
    };
  }, [token, key]);

  return { data, fresh: !!key && key === dataKey, loading, error };
}
